import Cookies from 'js-cookie';
import { ApiService } from '../api';
import { AnalyticsService } from '$lib/services/analytics';
import { user } from '$lib/stores/user/store';
import { app } from '$lib/stores/app/store';
import { USER_TYPE_ABBREVIATIONS } from '$lib/stores/user/constants';
import { get } from 'svelte/store';
import { Sentry } from '$lib/services/sentry';
import { COOKIES, OSU_REGPATH_PAGES, OSU_REG_STEPS_ENUMERATE } from '../../utils/constants';
import { getDeviceId } from '$lib/utils/device-id';
import { delay } from '$lib/utils/objects';
import { get1_0Account, has1_0Account, is1_0LegacyUser } from '$lib/utils/aofl-accounts';

/**
 * Experiment ID used for migration testing (control).
 * @constant {string}
 */
export const MIGRATION_TEST_EXPERIMENT_ID_CONTROL = '9100';
/**
 * Experiment ID used for migration testing (test).
 * @constant {string}
 */
export const MIGRATION_TEST_EXPERIMENT_ID_TEST = '9105';

/**
 * Service for handling user migration-related operations.
 */
export class MigrationService {
	/**
	 * Retrieves the user type from the API and caches it in a cookie.
	 * @returns {Promise<void>}
	 */
	static async getAndCacheUserType() {
		// Don't do any of this for pure-2.0 users.
		if (!has1_0Account()) {
			return;
		}
		const account = get1_0Account();

		Sentry.addBreadcrumb({
			category: 'migration',
			message: `User type: ${account?.accountType ?? ''}`,
			level: 'info'
		});

		if (is1_0LegacyUser()) {
			Cookies.set(COOKIES.MIGRATION_USER_TYPE, account?.accountType ?? '', COOKIES.CONFIG);
		}
	}

	/**
	 * Retrieves the user type from cookies.
	 * @returns {string|undefined} The user type or undefined if not set.
	 */
	static getUserType() {
		return Cookies.get(COOKIES.MIGRATION_USER_TYPE);
	}

	/**
	 * Checks if the current user is a migration user.
	 * @returns {boolean} True if the user is a migration user, false otherwise.
	 */
	static isMigrationUser() {
		const userType = MigrationService.getUserType();
		return typeof userType === 'string' && userType.includes(USER_TYPE_ABBREVIATIONS.LEGACY_USER);
	}

	/**
	 * Checks if the route is eligible for migration and appends the experiment ID if necessary.
	 * @param {object} response - The response object from the API.
	 * @returns {string|boolean} The modified URL with the experiment ID or false if not applicable.
	 */
	static async getRedirectUrl(response) {
		const nextStep = response?.regpath_login_info?.step;
		const isActive = response?.regpath_login_info?.is_active;
		const isAssessmentActive = response?.regpath_login_info?.is_active_assessment;

		let path;
		if (!isActive && !isAssessmentActive) {
			path = OSU_REGPATH_PAGES[OSU_REG_STEPS_ENUMERATE.REACTIVATE];
		} else {
			path = OSU_REGPATH_PAGES[nextStep];
		}

		const separator = path.includes('?') ? '&' : '?';
		const armParam =
			OSU_REGPATH_PAGES[OSU_REG_STEPS_ENUMERATE.COMPLETE] === path
				? `arm=${MIGRATION_TEST_EXPERIMENT_ID_TEST}`
				: `arm=${MIGRATION_TEST_EXPERIMENT_ID_CONTROL}`;
		return `${path || '/abc/'}${separator}${armParam}`;
	}

	/**
	 * Upgrades the user session and redirects to the specified URL.
	 * @returns {Promise<{ token: string; plog: string; validateLoginOSUResp: object } | null>}
	 */
	static async upgradeSession() {
		try {
			// TODO: add to transports potentially
			await AnalyticsService.trackMigrationRedirect({
				currentUrl: window.location.href,
				userType: MigrationService.getUserType()
			});
			const currentUser = get(user);
			const sessionResponse = await ApiService.createSessionsInLegacy({ token: currentUser.token });

			sessionResponse?.cookies?.map(
				(
					/** @type {{ name: string; value: string; secure: any; domain: any; expire: any; path: any; }} */ eachCookie
				) => {
					Cookies.set(eachCookie?.name, eachCookie?.value, {
						secure: eachCookie?.secure,
						domain: eachCookie?.domain,
						expires: eachCookie?.expire,
						path: eachCookie?.path
					});
				}
			);

			await delay(500);

			if (!sessionResponse?.token)
				MigrationService.issueLogicException('Invalid MWS Account response or missing token', {
					sessionResponse
				});

			try {
				Sentry.addBreadcrumb({
					category: 'migration',
					message: `Logging all cookies`,
					data: {
						cookiesFromSessionResponse: sessionResponse?.cookies,
						cookiesFromCookies: Cookies.get(),
						cookieString: document?.cookie
					},
					level: 'info'
				});
			} catch (error) {
				Sentry.captureException(error, {
					extra: {
						action: 'Error: Migration Service - Logging cookies Exception'
					}
				});
			}

			const token = sessionResponse?.cookies?.find((cookie) => cookie?.name === 'session')?.value;
			const plog = sessionResponse?.cookies?.find((cookie) => cookie?.name === 'plog')?.value;
			const validateLoginOSUResp = await ApiService.validateLoginOSU({ token, plog });

			if (!validateLoginOSUResp?.token_valid)
				MigrationService.issueLogicException('Invalid OSU login', {
					sessionResponse,
					validateLoginOSUResp
				});

			return { token, plog, validateLoginOSUResp };
		} catch (error) {
			Sentry.captureException(error, {
				extra: {
					action: 'Error: Migration Redirect Issue',
					userType: MigrationService.getUserType()
				}
			});

			return null;
		}
	}

	/**
	 * Redirects to the migration URL
	 * @param {object} validateLoginOSUResp - The response object from the API.
	 * @param {string} token - The token to use for the login.
	 * @param {string} plog - The plog to use for the login.
	 * @param {string} redirectUrl - The redirect URL to use for the login.
	 * @returns {Promise<void>}
	 */
	static async redirectToMigrationUrl(validateLoginOSUResp, token, plog, redirectUrl) {
		Sentry.addBreadcrumb({
			category: 'migration',
			message: `Redirecting to: ${redirectUrl}`,
			level: 'info'
		});

		const osuMemberId = validateLoginOSUResp?.regpath_login_info?.member_id;
		await ApiService.eventLogOSU({
			url: '@newSystemMigrationRedirectSuccess',
			kvp: {
				userType: MigrationService.getUserType() || 'no user type',
				currentUrl: window.location.href,
				redirectUrl,
				osuMemberId
			}
		});

		let addParams =
			`&user_type=${MigrationService.getUserType()}` +
			`&device_id=${getDeviceId()}` +
			`&mem=${osuMemberId}`;

		// Fix to address ABC-MKT-WEB-CLASSIC-56 in Sentry (AD)
		if (get(app).meta.inApp) {
			addParams = addParams + `&token=${token}` + `&plog=${plog}`;
		}

		window.location.href = redirectUrl + addParams;
	}

	/**
	 * @param {string} message - the name of the exception
	 * @param {object} relevantExtras - any relevant extras
	 */
	static issueLogicException(message, relevantExtras) {
		Sentry.captureException(message, {
			extra: {
				action: 'Error: Migration Service - Logic Exception',
				userType: MigrationService.getUserType(),
				user: get(user),
				...relevantExtras
			}
		});
	}

	/**
	 * Handles the migration process for a user.
	 *
	 * This method checks if the user is a migration user and, if so,
	 * verifies the redirect URL for migration. If a migration URL is found,
	 * it upgrades the session and redirects the user accordingly.
	 * @returns {Promise<{validateLoginOSUResp: object, token: string, plog: string, redirectUrl: string}>} A promise that resolves when the migration process is complete.
	 */
	static async handleMigrationUser() {
		await MigrationService.getAndCacheUserType();

		// If the user is not a migration user, return false
		if (!MigrationService.isMigrationUser()) return false;

		// Upgrade the session and redirect to the migration URL
		const upgradeSessionResponse = await MigrationService.upgradeSession();
		if (!upgradeSessionResponse) return false;

		const { validateLoginOSUResp, token, plog } = upgradeSessionResponse;
		const redirectUrl = await MigrationService.getRedirectUrl(validateLoginOSUResp);

		return { validateLoginOSUResp, token, plog, redirectUrl };
	}
}
